import { useEffect, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Nav from "../components/site/Nav";
import Footer from "../components/site/Footer";
import PortfolioCarousel from "../components/site/PortfolioCarousel";
import CaseStudyCard from "../components/site/CaseStudyCard";
import { workAssets } from "../components/site/Portfolio";
import { useT } from "../i18n/LanguageContext";
import { CONTACT } from "../lib/contact";
import useReveal from "../hooks/useReveal";
import { track } from "../lib/analytics";

const SITE_URL = "https://gfirem.com";
const DEFAULT_OG = `${SITE_URL}/og/default.jpg`;

// Per-slug og:image — points to the branded 1200x630 image in /public/og/
// so social scrapers (WhatsApp, LinkedIn, X, Slack, iMessage) see the correct
// artwork even without JavaScript.
function ogImageFor(asset) {
    if (!asset) return DEFAULT_OG;
    return `${SITE_URL}/og/case-study-${asset.slug}.jpg`;
}

// Manage <title>, meta description, canonical, og tags, and JSON-LD from React.
function useCaseStudyHead({ slug, title, intro, ogImage, lang, client, category, location, meta }) {
    useEffect(() => {
        if (!slug) return;
        const canonical = `${SITE_URL}/case-studies/${slug}`;
        const pageTitle = `${title} — Case study · ${client} | gfirem`;

        // helper to upsert head tags
        const upsert = (selector, create) => {
            let el = document.head.querySelector(selector);
            if (!el) {
                el = create();
                document.head.appendChild(el);
            }
            return el;
        };

        // Snapshot the values we're about to override so we can restore them on unmount.
        // This keeps Home's SEO tags intact when the user navigates back.
        const snapshot = {
            title: document.title,
            description: document.head.querySelector('meta[name="description"]')?.getAttribute("content") ?? null,
            canonical: document.head.querySelector('link[rel="canonical"]')?.getAttribute("href") ?? null,
            "og:type": document.head.querySelector('meta[property="og:type"]')?.getAttribute("content") ?? null,
            "og:url": document.head.querySelector('meta[property="og:url"]')?.getAttribute("content") ?? null,
            "og:title": document.head.querySelector('meta[property="og:title"]')?.getAttribute("content") ?? null,
            "og:description": document.head.querySelector('meta[property="og:description"]')?.getAttribute("content") ?? null,
            "og:image": document.head.querySelector('meta[property="og:image"]')?.getAttribute("content") ?? null,
            "og:locale": document.head.querySelector('meta[property="og:locale"]')?.getAttribute("content") ?? null,
            "twitter:card": document.head.querySelector('meta[name="twitter:card"]')?.getAttribute("content") ?? null,
            "twitter:title": document.head.querySelector('meta[name="twitter:title"]')?.getAttribute("content") ?? null,
            "twitter:description": document.head.querySelector('meta[name="twitter:description"]')?.getAttribute("content") ?? null,
            "twitter:image": document.head.querySelector('meta[name="twitter:image"]')?.getAttribute("content") ?? null,
        };

        document.title = pageTitle;
        document.documentElement.lang = lang === "es" ? "es" : "en";

        const desc = upsert('meta[name="description"]', () => {
            const m = document.createElement("meta");
            m.name = "description";
            return m;
        });
        desc.setAttribute("content", intro);

        const canon = upsert('link[rel="canonical"]', () => {
            const l = document.createElement("link");
            l.rel = "canonical";
            return l;
        });
        canon.setAttribute("href", canonical);

        const ogTags = [
            ["og:type", "article"],
            ["og:url", canonical],
            ["og:title", pageTitle],
            ["og:description", intro],
            ["og:image", ogImage],
            ["og:locale", lang === "es" ? "es_LA" : "en_US"],
        ];
        ogTags.forEach(([property, content]) => {
            const el = upsert(
                `meta[property="${property}"]`,
                () => {
                    const m = document.createElement("meta");
                    m.setAttribute("property", property);
                    return m;
                }
            );
            el.setAttribute("content", content);
        });

        const twTags = [
            ["twitter:card", "summary_large_image"],
            ["twitter:title", pageTitle],
            ["twitter:description", intro],
            ["twitter:image", ogImage],
        ];
        twTags.forEach(([name, content]) => {
            const el = upsert(
                `meta[name="${name}"]`,
                () => {
                    const m = document.createElement("meta");
                    m.setAttribute("name", name);
                    return m;
                }
            );
            el.setAttribute("content", content);
        });

        // Inject a dedicated JSON-LD block for this case study.
        const scriptId = "case-study-jsonld";
        let script = document.getElementById(scriptId);
        if (!script) {
            script = document.createElement("script");
            script.type = "application/ld+json";
            script.id = scriptId;
            document.head.appendChild(script);
        }
        const jsonld = {
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "Article",
                    "@id": `${canonical}#article-dynamic`,
                    mainEntityOfPage: canonical,
                    name: title,
                    headline: title,
                    description: intro,
                    url: canonical,
                    image: ogImage,
                    inLanguage: lang === "es" ? "es-LA" : "en-US",
                    genre: category,
                    keywords: (meta || []).join(", "),
                    about: client,
                    locationCreated: { "@type": "Place", name: location },
                    author: {
                        "@type": "Person",
                        "@id": `${SITE_URL}/#person`,
                        name: "Guillermo Figueroa Mesa",
                        url: SITE_URL,
                    },
                    creator: { "@id": `${SITE_URL}/#person` },
                    publisher: { "@id": `${SITE_URL}/#business` },
                    isPartOf: { "@id": `${SITE_URL}/#website` },
                },
                {
                    "@type": "BreadcrumbList",
                    "@id": `${canonical}#breadcrumbs-dynamic`,
                    itemListElement: [
                        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
                        { "@type": "ListItem", position: 2, name: "Case studies", item: `${SITE_URL}/case-studies` },
                        { "@type": "ListItem", position: 3, name: title, item: canonical },
                    ],
                },
            ],
        };
        script.textContent = JSON.stringify(jsonld);

        return () => {
            // Roll everything back so Home doesn't inherit case-study meta.
            const restoreAttr = (selector, attr, value) => {
                const el = document.head.querySelector(selector);
                if (el && value !== null) el.setAttribute(attr, value);
            };
            document.title = snapshot.title;
            restoreAttr('meta[name="description"]', "content", snapshot.description);
            restoreAttr('link[rel="canonical"]', "href", snapshot.canonical);
            restoreAttr('meta[property="og:type"]', "content", snapshot["og:type"]);
            restoreAttr('meta[property="og:url"]', "content", snapshot["og:url"]);
            restoreAttr('meta[property="og:title"]', "content", snapshot["og:title"]);
            restoreAttr('meta[property="og:description"]', "content", snapshot["og:description"]);
            restoreAttr('meta[property="og:image"]', "content", snapshot["og:image"]);
            restoreAttr('meta[property="og:locale"]', "content", snapshot["og:locale"]);
            restoreAttr('meta[name="twitter:card"]', "content", snapshot["twitter:card"]);
            restoreAttr('meta[name="twitter:title"]', "content", snapshot["twitter:title"]);
            restoreAttr('meta[name="twitter:description"]', "content", snapshot["twitter:description"]);
            restoreAttr('meta[name="twitter:image"]', "content", snapshot["twitter:image"]);

            const s = document.getElementById(scriptId);
            if (s && s.parentNode) s.parentNode.removeChild(s);
        };
    }, [slug, title, intro, ogImage, lang, client, category, location, meta]);
}

export default function CaseStudy() {
    useReveal();
    const { slug } = useParams();
    const navigate = useNavigate();
    const { t, lang } = useT();

    const asset = useMemo(
        () => workAssets.find((a) => a.slug === slug),
        [slug]
    );
    const index = useMemo(
        () => workAssets.findIndex((a) => a.slug === slug),
        [slug]
    );
    const copies = t("portfolio.works");
    const labels = t("portfolio.labels");
    const related = t("portfolio.related");
    const copy = index >= 0 ? copies[index] : null;

    // Pick up to 3 related case studies: prefer same-industry siblings, fill with others by proximity.
    const relatedItems = useMemo(() => {
        if (!asset) return [];
        const others = workAssets
            .map((a, i) => ({ asset: a, copy: copies[i], i }))
            .filter((x) => x.asset.slug !== asset.slug);
        const sameIndustry = others.filter(
            (x) => x.asset.industry === asset.industry
        );
        const rest = others.filter((x) => x.asset.industry !== asset.industry);
        return [...sameIndustry, ...rest].slice(0, 3);
    }, [asset, copies]);

    useCaseStudyHead({
        slug,
        title: copy?.title || "",
        intro: copy?.intro || "",
        ogImage: ogImageFor(asset),
        lang,
        client: copy?.client || "",
        category: copy?.category || "",
        location: copy?.location || "",
        meta: copy?.meta || [],
    });

    // If slug doesn't match a known case study, bounce back home.
    useEffect(() => {
        if (!asset) navigate("/", { replace: true });
    }, [asset, navigate]);

    if (!asset || !copy) return null;

    return (
        <main data-testid="case-study-page" data-cta-source="case_study_detail" className="bg-sand text-ink">
            <div className="fixed top-0 left-0 right-0 z-50">
                <Nav />
            </div>

            <article className="pt-28 md:pt-36 pb-20 md:pb-28">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="reveal">
                        <Link
                            to="/#work"
                            data-testid="case-study-back-link"
                            className="inline-flex items-center gap-2 text-xs font-mono tracking-[0.18em] uppercase text-secondary hover:text-ink transition-colors"
                        >
                            <span aria-hidden>←</span>
                            {t("portfolio.backToHome")}
                        </Link>
                    </div>

                    <header className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
                        <div className="lg:col-span-8 reveal">
                            <span className="font-mono text-terracotta text-sm tracking-[0.16em]">
                                {copy.n} · {copy.client}
                            </span>
                            <div className="overline mt-3">
                                {copy.category} · {copy.location}
                            </div>
                            <h1
                                data-testid="case-study-title"
                                className="mt-5 font-serif text-4xl sm:text-5xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.05] text-ink"
                            >
                                {copy.title}
                            </h1>
                            <p className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-secondary">
                                {copy.intro}
                            </p>
                        </div>
                        <aside className="lg:col-span-4 reveal">
                            <dl className="border-t border-warm pt-5 space-y-4 text-sm">
                                <div>
                                    <dt className="overline">{t("portfolio.sidebar.client")}</dt>
                                    <dd className="mt-1 text-ink">{copy.client}</dd>
                                </div>
                                <div>
                                    <dt className="overline">{t("portfolio.sidebar.category")}</dt>
                                    <dd className="mt-1 text-ink">{copy.category}</dd>
                                </div>
                                <div>
                                    <dt className="overline">{t("portfolio.sidebar.location")}</dt>
                                    <dd className="mt-1 text-ink">{copy.location}</dd>
                                </div>
                                {asset.href ? (
                                    <div>
                                        <dt className="overline">{t("portfolio.sidebar.liveSite")}</dt>
                                        <dd className="mt-1">
                                            <a
                                                href={asset.href}
                                                target="_blank"
                                                rel="noreferrer"
                                                data-testid="case-study-live-link"
                                                onClick={() =>
                                                    track("live_site_visited", {
                                                        slug: asset.slug,
                                                        industry: asset.industry,
                                                        source: "detail_sidebar",
                                                        url: asset.href,
                                                    })
                                                }
                                                className="link-underline text-ink"
                                            >
                                                {asset.href.replace(/^https?:\/\//, "").replace(/\/$/, "")} →
                                            </a>
                                        </dd>
                                    </div>
                                ) : (
                                    <div>
                                        <dt className="overline">{t("portfolio.sidebar.access")}</dt>
                                        <dd
                                            data-testid="case-study-private"
                                            className="mt-1 text-xs uppercase tracking-[0.18em] font-mono text-secondary"
                                        >
                                            {t("portfolio.privateNote")}
                                        </dd>
                                    </div>
                                )}
                            </dl>
                            <div className="mt-6 flex flex-wrap gap-2">
                                {copy.meta.map((m) => (
                                    <span
                                        key={m}
                                        className="text-[0.68rem] font-mono tracking-wide uppercase border border-warm text-ink/80 px-3 py-1"
                                    >
                                        {m}
                                    </span>
                                ))}
                            </div>
                        </aside>
                    </header>

                    <div className="mt-14 md:mt-20 reveal">
                        <PortfolioCarousel
                            images={asset.images}
                            alt={copy.client}
                            href={asset.href}
                            kind={asset.kind}
                            testid="case-study-carousel"
                        />
                    </div>

                    <div className="mt-14 md:mt-20 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
                        <CaseSection
                            testid="case-study-problem"
                            label={labels.problem}
                            body={copy.problem}
                        />
                        <CaseSection
                            testid="case-study-solution"
                            label={labels.solution}
                            body={copy.solution}
                        />
                        <CaseSection
                            testid="case-study-outcome"
                            label={labels.outcome}
                            body={copy.outcome}
                        />
                    </div>

                    <div className="mt-20 md:mt-28 reveal border border-warm bg-[var(--sand)] p-8 md:p-12 lg:p-14">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                            <div className="lg:col-span-7">
                                <p className="overline mb-4">{t("contact.overline")}</p>
                                <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tighter leading-[1.05] text-ink">
                                    {t("contact.headlineA")}{" "}
                                    <em className="italic font-light text-terracotta">
                                        {t("contact.headlineEm")}
                                    </em>
                                </h2>
                                <p className="mt-4 max-w-xl text-base leading-relaxed text-secondary">
                                    {t("contact.headlineB")}
                                </p>
                            </div>
                            <div className="lg:col-span-5 flex flex-wrap gap-4">
                                <a
                                    href={CONTACT.calendlyHref}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="case-study-calendly-cta"
                                    className="inline-flex items-center gap-3 bg-ink text-sand px-6 py-3.5 text-sm tracking-wide hover:bg-[var(--ink-soft)] transition-colors"
                                >
                                    {t("contact.ctaCall")}
                                    <span aria-hidden>→</span>
                                </a>
                                <a
                                    href={CONTACT.whatsappHref}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="case-study-whatsapp-cta"
                                    className="inline-flex items-center gap-3 border border-ink text-ink px-6 py-3.5 text-sm tracking-wide hover:bg-ink hover:text-sand transition-colors"
                                >
                                    {t("contact.ctaWhatsapp")}
                                </a>
                            </div>
                        </div>
                    </div>

                    {relatedItems.length > 0 && (
                        <section
                            data-testid="related-case-studies"
                            className="mt-24 md:mt-32 reveal"
                        >
                            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
                                <div>
                                    <p className="overline mb-3">
                                        {related.title}
                                    </p>
                                    <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl tracking-tight leading-tight text-ink">
                                        {related.subtitle}
                                    </h2>
                                </div>
                                <Link
                                    to="/case-studies"
                                    data-testid="related-see-all-link"
                                    className="text-xs uppercase tracking-[0.18em] font-mono text-ink hover:text-terracotta transition-colors link-underline"
                                >
                                    {t("portfolio.seeAll")} →
                                </Link>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                                {relatedItems.map(({ asset: a, copy: c, i }) => (
                                    <CaseStudyCard
                                        key={a.slug}
                                        asset={a}
                                        copy={c}
                                        index={i}
                                        testid={`related-card-${a.slug}`}
                                        source="related"
                                    />
                                ))}
                            </div>
                        </section>
                    )}
                </div>
            </article>

            <Footer />
        </main>
    );
}

const CaseSection = ({ label, body, testid }) => (
    <section data-testid={testid} className="reveal">
        <div className="flex items-center gap-3">
            <span aria-hidden className="inline-block w-6 h-px bg-terracotta" />
            <p className="font-mono text-[0.72rem] uppercase tracking-[0.2em] text-ink">
                {label}
            </p>
        </div>
        <p className="mt-4 text-base md:text-[1.02rem] leading-relaxed text-secondary">
            {body}
        </p>
    </section>
);
